import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarDays, MapPin, Clock } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { key: "all", label: "All" },
  { key: "practice", label: "Practices" },
  { key: "game", label: "Games" },
  { key: "event", label: "Events" },
  { key: "registration", label: "Registration" },
];

const CATEGORY_STYLES = {
  practice: "bg-[#0F4D22] text-white",
  game: "bg-[#FEE123] text-black",
  event: "bg-white/10 text-white",
  registration: "bg-[#FEE123]/20 text-[#FEE123]",
};

export default function Schedule() {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/schedule`)
      .then((res) => setEvents(res.data))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  const visible = filter === "all" ? events : events.filter((e) => e.category === filter);

  return (
    <>
      <PageHeader
        eyebrow="Season Calendar"
        title="Schedule"
        description="Coming soon."
        testid="schedule-header"
      />
      <section className="py-14 sm:py-20 bg-[#0A0A0A]" data-testid="schedule-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 mb-12" data-testid="schedule-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`schedule-filter-${f.key}`}
                className={`text-sm font-bold uppercase tracking-widest px-5 py-2.5 rounded-sm border transition-colors duration-200 ${
                  filter === f.key
                    ? "bg-[#FEE123] text-black border-[#FEE123]"
                    : "bg-transparent text-gray-300 border-[#333333] hover:border-[#FEE123]/50 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-gray-400 uppercase tracking-widest text-sm" data-testid="schedule-loading">Loading schedule...</p>
          ) : visible.length === 0 ? (
            <p className="text-gray-400 uppercase tracking-widest text-sm" data-testid="schedule-empty">No events in this category yet.</p>
          ) : (
            <div className="space-y-4" data-testid="schedule-list">
              {visible.map((event, i) => (
                <Reveal key={event.id} delay={i * 0.06}>
                  <div
                    className="flex flex-col md:flex-row md:items-center gap-5 bg-[#111111] border border-[#333333] p-6 sm:p-7 transition-colors duration-200 hover:border-[#FEE123]/40"
                    data-testid={`schedule-event-${i}`}
                  >
                    <div className="md:w-64 shrink-0">
                      <span className={`inline-block text-[11px] font-extrabold uppercase tracking-widest px-3 py-1 rounded-sm ${CATEGORY_STYLES[event.category] || CATEGORY_STYLES.event}`}>
                        {event.category}
                      </span>
                      <p className="mt-3 flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-[#FEE123]">
                        <CalendarDays size={16} /> {event.date_label}
                      </p>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold uppercase tracking-wide text-white">{event.title}</h3>
                      {event.description && <p className="mt-2 text-sm text-gray-400 leading-relaxed">{event.description}</p>}
                    </div>
                    <div className="md:w-56 shrink-0 text-sm text-gray-300 space-y-1.5">
                      <p className="flex items-center gap-2"><Clock size={15} className="text-[#FEE123]" /> {event.time_label}</p>
                      <p className="flex items-center gap-2"><MapPin size={15} className="text-[#FEE123]" /> {event.location}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          )}

          <p className="mt-12 text-xs uppercase tracking-widest text-gray-500" data-testid="schedule-disclaimer">
            The Louisiana Ducks' inaugural season kicks off in 2027. The full game schedule will be posted here once dates are confirmed.
          </p>
        </div>
      </section>
    </>
  );
}
